import { Col, Icon, Switch, Tappable, Text, useTheme } from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { calledBy } from "../../me/called.ts";
import { RouteFace, type Who } from "./route-line.tsx";

const A_ROW_IS = 40;

const AS_WIDE_AS = 260;

const WORDS = { typed: "route.typed", likely: "route.likely", unlikely: "route.unlikely" } as const;

function RouteChoice({ one, onPress }: { one: Who; onPress: (() => void) | null }) {
  const theme = useTheme();
  const name = calledBy(one.who) ?? one.who.handle;
  const going = one.word !== "unlikely";
  const inside = (
    <>
      <RouteFace who={one.who} size={20} />
      <Text typeName="body" numberOfLines={1} style={{ flex: 1, minWidth: 0 }}>
        {name}
      </Text>
      <Text typeName="caption" color="text.tertiary">
        {say(WORDS[one.word])}
      </Text>
      {going ? (
        <Icon name="check" size={14} color={one.word === "typed" ? "accent" : "text.secondary"} />
      ) : (
        <Col style={{ width: 14, alignItems: "center" }}>
          <Col
            style={{ width: 4, height: 4, borderRadius: 999, backgroundColor: theme.color["text.tertiary"] }}
          />
        </Col>
      )}
    </>
  );
  const skin = {
    minHeight: A_ROW_IS,
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: theme.space[2],
    paddingHorizontal: theme.space[3],
  };

  if (!onPress) {
    return (
      <Col testID={`route-row-${one.who.handle}`} style={skin}>
        {inside}
      </Col>
    );
  }
  return (
    <Tappable
      accessibilityRole="button"
      accessibilityLabel={going ? say("route.drop")(name) : say("route.add")(name)}
      testID={`route-row-${one.who.handle}`}
      onPress={onPress}
      {...({ onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() } as object)}
      style={{
        ...skin,
      }}
    >
      {inside}
    </Tappable>
  );
}

export function RoutePanel({
  rows,
  on,
  onDrop,
  onAdd,
  onAuto,
}: {
  rows: readonly Who[];
  on: boolean;
  onDrop: (handle: string) => void;
  onAdd: (handle: string) => void;
  onAuto: (on: boolean) => void;
}) {
  const theme = useTheme();
  return (
    <Col
      testID="route-panel"
      accessibilityLabel={say("route.goesTo")}
      style={{
        position: "absolute",

        bottom: "100%",
        right: 0,
        marginBottom: theme.space[2],
        width: AS_WIDE_AS,
        paddingVertical: theme.space[2],
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        backgroundColor: theme.color["bg.surface"],
        borderRadius: theme.radius.lg,
        overflow: "hidden",
        ...theme.elevation.raised,
      }}
    >
      <Text
        typeName="caption"
        color="text.tertiary"
        style={{ paddingHorizontal: theme.space[3], paddingBottom: theme.space[1] }}
      >
        {say("route.goesTo")}
      </Text>
      {rows.map((one) => (
        <RouteChoice
          key={one.who.account}
          one={one}
          onPress={
            one.word === "typed"
              ? null
              : one.word === "likely"
                ? () => onDrop(one.who.handle)
                : () => onAdd(one.who.handle)
          }
        />
      ))}
      <Col
        style={{
          height: 1,
          backgroundColor: theme.color["border.default"],
          marginVertical: theme.space[2],
        }}
      />
      <Col
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[2],
          paddingHorizontal: theme.space[3],
        }}
      >
        <Text typeName="caption" color="text.secondary" style={{ flex: 1, minWidth: 0 }}>
          {say("route.auto")}
        </Text>
        <Switch value={on} onChange={onAuto} label={say("route.auto")} />
      </Col>
    </Col>
  );
}
